/**
 * 📅 Calendar Connect Prompt
 * Shows on first launch to prompt users to connect Google Calendar
 * Appears after user logs in for the first time or hasn't connected calendar
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Calendar, CheckCircle2, X, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CalendarConnectPrompt = ({ isOpen, onClose, onSkip }) => {
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const response = await fetch(`${API_URL}/api/auth/google/calendar-login`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.authorization_url) {
        // Save that we've shown the prompt
        localStorage.setItem('calendar_prompt_shown', 'true');
        localStorage.setItem('calendar_return_url', window.location.href);
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      console.error('Error connecting calendar:', error);
      setConnecting(false);
    }
  };

  const handleSkip = () => {
    // Save that user skipped - don't show for 7 days
    localStorage.setItem('calendar_prompt_skipped', Date.now().toString());
    localStorage.setItem('calendar_prompt_shown', 'true');
    if (onSkip) onSkip();
    if (onClose) onClose();
  };

  const handleRemindLater = () => {
    // Show again in 24 hours
    localStorage.setItem('calendar_prompt_remind_later', Date.now().toString());
    if (onClose) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center">
                <Calendar className="h-10 w-10 text-blue-600" />
              </div>
              <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            Connect Google Calendar? 📅
          </DialogTitle>
          <DialogDescription className="text-center">
            Keep everything synced across all your devices
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Benefits */}
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-900">Two-Way Sync</p>
                <p className="text-sm text-blue-700">Your Google Calendar events appear in LifeFlo, and your LifeFlo items sync to Google</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-900">Never Miss Anything</p>
                <p className="text-sm text-green-700">Tasks, appointments, medicine reminders & bills — all in one place</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-teal-900">AI Smart Categorization</p>
                <p className="text-sm text-teal-700">Events are automatically sorted into Work, Health, or Life modes</p>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="space-y-2 pt-2">
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Connecting...
                </>
              ) : (
                <>
                  <Calendar className="h-5 w-5 mr-2" />
                  Connect Google Calendar
                </>
              )}
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleRemindLater}
              >
                Remind Me Later
              </Button>
              <Button
                variant="ghost"
                className="flex-1 text-gray-500"
                onClick={handleSkip}
              >
                Skip for Now
              </Button>
            </div>
          </div>
          
          <p className="text-xs text-center text-gray-400">
            You can always connect later from Settings
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper hook to manage when to show the prompt
export const useCalendarPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);

  useEffect(() => {
    checkIfShouldShowPrompt();
  }, []);

  const checkIfShouldShowPrompt = async () => {
    try {
      // First check if already connected
      const response = await fetch(`${API_URL}/api/calendar/status`, {
        credentials: 'include'
      });
      
      // Don't try to parse JSON if not OK (e.g., 401 unauthorized)
      if (!response.ok) {
        console.log('Calendar status check failed, skipping prompt');
        setShowPrompt(false);
        return;
      }
      
      const status = await response.json();
      
      if (status.connected) {
        setCalendarConnected(true);
        setShowPrompt(false);
        return;
      }

      // Check if user just returned from OAuth
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('calendar_connected') === 'true') {
        setCalendarConnected(true);
        setShowPrompt(false);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      // Check if prompt was already shown
      const promptShown = localStorage.getItem('calendar_prompt_shown');
      const skippedAt = localStorage.getItem('calendar_prompt_skipped');
      const remindLaterAt = localStorage.getItem('calendar_prompt_remind_later');

      // If skipped, don't show for 7 days
      if (skippedAt) {
        const daysSinceSkip = (Date.now() - parseInt(skippedAt)) / (1000 * 60 * 60 * 24);
        if (daysSinceSkip < 7) {
          setShowPrompt(false);
          return;
        }
      }

      // If remind later, don't show for 24 hours
      if (remindLaterAt) {
        const hoursSinceRemind = (Date.now() - parseInt(remindLaterAt)) / (1000 * 60 * 60);
        if (hoursSinceRemind < 24) {
          setShowPrompt(false);
          return;
        }
      }

      // Show prompt if not connected and not recently dismissed
      if (!promptShown || (skippedAt && (Date.now() - parseInt(skippedAt)) > 7 * 24 * 60 * 60 * 1000)) {
        // Small delay for better UX
        setTimeout(() => setShowPrompt(true), 2000);
      }
    } catch (error) {
      console.error('Error checking calendar prompt:', error);
    }
  };

  const closePrompt = () => setShowPrompt(false);

  return { showPrompt, closePrompt, calendarConnected };
};

export default CalendarConnectPrompt;
